import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuthStore } from "./store/useAuthStore";
import { useChatStore } from "./store/useChatStore";
import { connectSocket, initSocketNotifications } from "./socket";
import { ensureNotificationPermission } from "./lib/notifications";
import AdminLayout from "./components/AdminLayout";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import LoginForm from "./components/LoginForm";
import EmployeeForm from "./components/EmployeeForm";
import AdminDashboard from "./components/AdminDashboard";
import EmployeeLogDetail from "./components/EmployeeLogDetail";
import DrawingMaster from "./components/DrawingMaster";
import ManageEmployees from "./components/ManageEmployees";
import LeaveApplication from "./components/LeaveApplication";
import EmployeeDashboard from "./components/EmployeeDashboard";
import Checklist from "./components/Checklist";
import HomePage from "./pages/HomePage";
import ProjectCommunication from "./pages/ProjectCommunication";
import CategoryCommunication from "./pages/CategoryCommunication";
import Profile from "./pages/ProfilePage";
import ProjectCostReport from "./components/ProjectCostReport";
import EmployeeWorkDetails from "./components/EmployeeWorkDetails";
import AddProject from "./components/AddProject";
import Transmittal from "./components/Transmittal";
import TransmittalList from "./components/TransmittalList";
import LeaveRequest from "./components/LeaveRequest";
import AdminLogSummary from "./components/AdminLogSummary";
import AttendancePage from "./components/AttendancePage";
import "./styles/responsive.css";
import SoftwareAdmin from "./pages/SoftwareAdmin";
import SoftwareEmployee from "./pages/SoftwareEmployee";
import SoftwareRenewalHistory from "./pages/SoftwareRenewalHistory";
import SoftwareArchived from "./pages/SoftwareArchived";
import HardwareAdmin from "./pages/HardwareAdmin";
import HardwareEmployee from "./pages/HardwareEmployee";
import HardwareDestroyed from "./pages/HardwareDestroyed";
import EmployeeReview from "./components/EmployeeReviews";
import AdminEmployeeReviewDetail from "./components/AdminEmployeeReviewDetail";
import ExpenseGraphDashboard from "./pages/ExpenseGraphDashboard";
import RolePermissionPage from "./pages/RolePermissionPage";
import EmployeeDrawingReport from "./pages/EmployeeDrawingReport";
import ActiveSessions from "./pages/ActiveSessions";
import OperationsDashboard from "./components/OperationsDashboard";
import FinanceEntry from "./components/FinanceEntry";
import ConstructionReport from "./components/ConstructionReport";
import SavedConstructionReports from "./components/SavedConstructionReports";
import ProjectManagmentDashboard from "./pages/ProjectManagmentDashboard";
import TaskBoard from "./components/TaskBoard";
import GanttChart from "./components/GanttChart";
import Leads from "./Crm/Leads";
import LeadDetail from "./Crm/LeadDetail";
import Proposals from "./Crm/Proposals";
import SentEmails from "./Crm/SentEmails";
import WonLeads from "./Crm/WonLeads";
import LostLeads from "./Crm/LostLeads";
import Reports from "./Crm/Reports";
import CrmDashboard from "./Crm/CrmDashboard";
import HRDashboard from "./HR/HRDashboard";
import JobOpenings from "./HR/JobOpenings";
import Candidates from "./HR/Candidates";
import CandidateDetail from "./HR/CandidateDetail";
import Interviews from "./HR/Interviews";
import Offers from "./HR/Offers";
import Joining from "./HR/Joining";
import ClosedHiring from "./HR/ClosedHiring";
import RecruitmentReports from "./HR/RecruitmentReports";
import { api } from "./api";
import { can } from "./utils/rolePermission";
import ProjectHoursSummary from "./components/ProjectHoursSummary";
import CampaignMaster from "./Crm/CampaignMaster";
import SalesDashboard from "./SalesInvoice/SalesDashboard";
import SalesMasterPage from "./SalesInvoice/SalesMasterPage";
import ProformaInvoicePage from "./SalesInvoice/ProformaInvoicePage";
import QuotationPage from "./SalesInvoice/QuotationPage";
import InvoicePage from "./SalesInvoice/InvoicePage";
import NotesPage from "./Notes/NotesPage";
import NoteEditorPage from "./Notes/NoteEditorPage";
import GSTReport from "./components/GSTReport";
import TDSReport from "./components/TDSReport";
import PurchaseDashboard from "./Purchase/Dashboard";
import PurchaseQuotationPage from "./Purchase/QuotationPage";
import PurchaseProformaInvoicePage from "./Purchase/ProformaInvoicePage";
import PurchaseInvoicePage from "./Purchase/InvoicePage";
import PurchaseVendorMaster from "./Purchase/VendorMaster";
import MarketingDashboard from "./Marketing/MarketingDashboard";
import CreatePost from "./Marketing/CreatePost";
import EmailPage from "./Email/EmailPage";

import CreateMailAccount from "./MailServer/CreateMailAccount";

/* ========================= ADMIN SHELL ========================= */

function AdminShell({ user }) {
  const location = useLocation();

  const isFinanceEntry =
    location.pathname === "/finance-entry" ||
    location.pathname.startsWith("/finance-entry/");

  const routeContent = (
    <Routes>
      <Route index element={<AdminDashboard />} />
      <Route path="admin/log-summary" element={<AdminLogSummary />} />
      <Route path="admin/logs/:userId" element={<EmployeeLogDetail />} />
      <Route
        path="operations-dashboard"
        element={<OperationsDashboard user={user} />}
      />
      <Route path="checklist" element={<Checklist />} />
      <Route path="drawing-master" element={<DrawingMaster />} />
      <Route path="manage-employees" element={<ManageEmployees />} />
      <Route path="project-cost-report" element={<ProjectCostReport />} />
      <Route path="leave-application" element={<LeaveRequest />} />
      <Route path="attendance-page" element={<AttendancePage />} />

      <Route path="messenger" element={<HomePage />} />
      <Route path="messenger/user-communication" element={<HomePage />} />
      <Route
        path="messenger/project-communication"
        element={<ProjectCommunication />}
      />
      <Route
        path="messenger/category-communication"
        element={<CategoryCommunication />}
      />
      <Route path="email" element={<Navigate to="/email/inbox" replace />} />
      <Route path="email/:folder" element={<EmailPage />} />

      <Route path="projects/new" element={<AddProject />} />

      <Route path="transmittal" element={<Transmittal />} />
      <Route path="transmittal/:id" element={<Transmittal />} />
      <Route path="transmittals" element={<TransmittalList />} />

      <Route path="software" element={<SoftwareAdmin />} />
      <Route path="software/archived" element={<SoftwareArchived />} />
      <Route
        path="software/renewal-history/:id"
        element={<SoftwareRenewalHistory />}
      />
      <Route path="my-software" element={<SoftwareEmployee />} />

      <Route path="hardware" element={<HardwareAdmin />} />
      <Route path="hardware-destroyed" element={<HardwareDestroyed />} />

      <Route
        path="admin-employee-review-detail"
        element={<AdminEmployeeReviewDetail />}
      />
      <Route
        path="/company-expenses/svb"
        element={<ExpenseGraphDashboard companyKey="svb" />}
      />
      <Route
        path="/company-expenses/sdc"
        element={<ExpenseGraphDashboard companyKey="sdc" />}
      />
      <Route
        path="/company-expenses/magnira"
        element={<ExpenseGraphDashboard companyKey="magnira" />}
      />
      <Route path="role-permissions" element={<RolePermissionPage />} />
      <Route
        path="/employee-drawing-report"
        element={<EmployeeDrawingReport />}
      />
      <Route path="finance-entry" element={<FinanceEntry user={user} />} />
      <Route
        path="construction-report"
        element={<ConstructionReport user={user} />}
      />
      <Route
        path="/construction-reports/saved"
        element={<SavedConstructionReports />}
      />
      <Route
        path="project-management-dashboard"
        element={<ProjectManagmentDashboard />}
      />
      <Route path="task-board" element={<TaskBoard />} />
      <Route path="gantt-chart" element={<GanttChart />} />
      <Route path="crm-dashboard" element={<CrmDashboard />} />
      <Route path="leads" element={<Leads />} />
      <Route path="/campaign-master" element={<CampaignMaster />} />

      <Route path="/leads/:id" element={<LeadDetail />} />
      <Route path="/proposals" element={<Proposals />} />
      <Route
        path="/sent-emails/intro"
        element={<SentEmails emailType="intro" />}
      />
      <Route path="/wons-leads" element={<WonLeads />} />
      <Route path="/lost-leads" element={<LostLeads />} />
      <Route path="/reports" element={<Reports />} />

      <Route
        path="/sent-emails/portfolio"
        element={<SentEmails emailType="portfolio" />}
      />

      <Route
        path="/sent-emails/proposal"
        element={<SentEmails emailType="proposal" />}
      />
      <Route path="/hr-dashboard" element={<HRDashboard />} />
      <Route path="/hr/job-openings" element={<JobOpenings />} />
      <Route path="/hr/candidates" element={<Candidates />} />
      <Route path="/hr/candidates/:id" element={<CandidateDetail />} />
      <Route path="/hr/interviews" element={<Interviews />} />
      <Route path="/hr/offers" element={<Offers />} />
      <Route path="/hr/joining" element={<Joining />} />
      <Route path="/hr/closed-hiring" element={<ClosedHiring />} />
      <Route path="/hr/recruitment-reports" element={<RecruitmentReports />} />
      <Route path="sales/dashboard" element={<SalesDashboard />} />

      <Route
        path="sales/services"
        element={<SalesMasterPage type="services" />}
      />

      <Route
        path="sales/clients"
        element={<SalesMasterPage type="clients" />}
      />

      <Route path="sales/banks" element={<SalesMasterPage type="banks" />} />

      <Route path="sales/terms" element={<SalesMasterPage type="terms" />} />

      <Route path="sales/quotations" element={<QuotationPage />} />
      <Route path="sales/proforma-invoices" element={<ProformaInvoicePage />} />
      <Route path="sales/invoices" element={<InvoicePage />} />
      <Route path="/tools/notes" element={<NotesPage />} />
      <Route path="/tools/notes/:noteId" element={<NoteEditorPage />} />
      <Route
        path="/finance/reports/gst/r1-sales"
        element={<GSTReport initialTab="sales" />}
      />

      <Route
        path="/finance/reports/gst/3b-purchase"
        element={<GSTReport initialTab="purchase" />}
      />
      <Route
        path="finance/reports/tds/payable"
        element={<TDSReport initialTab="payable" />}
      />

      <Route
        path="finance/reports/tds/receivable"
        element={<TDSReport initialTab="receivable" />}
      />
      {/* ================= PURCHASE ROUTES ================= */}
      <Route path="/purchase/dashboard" element={<PurchaseDashboard />} />

      <Route path="/purchase/quotations" element={<PurchaseQuotationPage />} />

      <Route
        path="/purchase/proforma-invoices"
        element={<PurchaseProformaInvoicePage />}
      />

      <Route path="/purchase/invoices" element={<PurchaseInvoicePage />} />

      <Route path="/purchase/vendors" element={<PurchaseVendorMaster />} />
      {/* ================= END PURCHASE ROUTES ================= */}
      <Route path="*" element={<Navigate to="/" replace />} />
      {/* ================= MARKETING ROUTES ================= */}

      <Route path="/marketing/dashboard" element={<MarketingDashboard />} />
      <Route path="/marketing/create-post" element={<CreatePost />} />
      {/* ================= END MARKETING ROUTES ================= */}

      <Route path="hr/create-mail-account" element={<CreateMailAccount />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  // Income & Expense Entry gets full width without the ERP sidebar.
  if (isFinanceEntry) {
    return (
      <div
        className="finance-entry-full-shell"
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          minHeight: "calc(100vh - 64px)",
          paddingTop: 64,
          boxSizing: "border-box",
          background: "#eef2f7",
          overflowX: "hidden",
        }}
      >
        {routeContent}
      </div>
    );
  }

  return <AdminLayout unreadCount={0}>{routeContent}</AdminLayout>;
}

/* ====================== NON-ADMIN SHELL ======================= */

function NonAdminShell({ user, isAccountant, isClient }) {
  const location = useLocation();

  const isFinanceEntry =
    location.pathname === "/finance-entry" ||
    location.pathname.startsWith("/finance-entry/");

  const showEmployeeSidebar = !isClient && !isFinanceEntry;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  /*
   * Current logged-in employee ki saved role permissions load karta hai.
   * Isse routes sirf hardcoded Accountant role par depend nahi rahenge.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchMyPermissions = async () => {
      if (!user?._id || !user?.role) {
        if (isMounted) {
          setPermissions({});
          setPermissionsLoading(false);
        }

        return;
      }

      try {
        if (isMounted) {
          setPermissionsLoading(true);
        }

        const response = await api.get("/roles/my-permissions", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (isMounted) {
          setPermissions(response.data?.permissions || {});
        }
      } catch (error) {
        console.error("Failed to fetch route permissions:", error);

        if (isMounted) {
          setPermissions({});
        }
      } finally {
        if (isMounted) {
          setPermissionsLoading(false);
        }
      }
    };

    fetchMyPermissions();

    return () => {
      isMounted = false;
    };
  }, [user?._id, user?.role]);

  const hasPermission = (moduleKey, action = "view") =>
    can(user, permissions, moduleKey, action);

  /*
   * Accountant ka existing access preserve kiya gaya hai.
   * Saath hi Role Permission page se diya gaya access bhi work karega.
   */
  const canViewSoftwareAllStaff =
    isAccountant || hasPermission("softwareAllStaff", "view");

  const canViewHardwareAllStaff =
    isAccountant || hasPermission("hardwareAllStaff", "view");

  if (permissionsLoading) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 64px)",
          paddingTop: 64,
          display: "grid",
          placeItems: "center",
          background: "#eef2f7",
          color: "#475569",
          fontWeight: 700,
        }}
      >
        Loading permissions...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile
          ? "1fr"
          : showEmployeeSidebar
            ? "260px 1fr"
            : "1fr",
        minHeight: "calc(100vh - 64px)",
        paddingTop: 64,
        background: "#eef2f7",
      }}
    >
      {showEmployeeSidebar && <Sidebar user={user} />}

      <div
        style={{
          padding: isFinanceEntry ? 0 : 20,
          paddingBottom: isFinanceEntry ? 0 : 80,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Routes>
          {!isClient && (
            <Route index element={<EmployeeDashboard user={user} />} />
          )}
          {isClient && (
            <Route index element={<Navigate to="messenger" replace />} />
          )}

          <Route
            path="timesheet"
            element={
              !isClient && hasPermission("timesheet", "view") ? (
                <EmployeeForm user={user} financeDefault={isAccountant} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="tools/notes"
            element={
              !isClient && hasPermission("notes", "view") ? (
                <NotesPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="tools/notes/:noteId"
            element={
              !isClient && hasPermission("notes", "view") ? (
                <NoteEditorPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="employee/logs/:userId"
            element={
              !isClient && hasPermission("employeeLog", "view") ? (
                <EmployeeLogDetail />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="operations-dashboard"
            element={<OperationsDashboard user={user} />}
          />
          <Route
            path="leave-application"
            element={
              !isClient && hasPermission("leaveApplication", "view") ? (
                <LeaveApplication />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="checklist" element={<Checklist />} />

          <Route
            path="EmployeeWorkDetails"
            element={
              isAccountant ? (
                <EmployeeWorkDetails user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="messenger" element={<HomePage />} />
          <Route path="messenger/user-communication" element={<HomePage />} />
          <Route
            path="messenger/project-communication"
            element={<ProjectCommunication />}
          />
          <Route
            path="messenger/category-communication"
            element={<CategoryCommunication />}
          />

          {/* ================= EMAIL (permission: email) ================= */}
          <Route
            path="email"
            element={
              !isClient && hasPermission("email", "view") ? (
                <Navigate to="/email/inbox" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="email/:folder"
            element={
              !isClient && hasPermission("email", "view") ? (
                <EmailPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* ============ CREATE MAIL ACCOUNT (permission: createMailAccount) ============ */}
          <Route
            path="hr/create-mail-account"
            element={
              !isClient && hasPermission("createMailAccount", "view") ? (
                <CreateMailAccount />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="profile" element={<Profile />} />

          <Route
            path="projects/new"
            element={isClient ? <Navigate to="/" replace /> : <AddProject />}
          />
          <Route
            path="company-expenses/svb"
            element={
              isAccountant ? (
                <ExpenseGraphDashboard companyKey="svb" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="company-expenses/sdc"
            element={
              isAccountant ? (
                <ExpenseGraphDashboard companyKey="sdc" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="company-expenses/magnira"
            element={
              isAccountant ? (
                <ExpenseGraphDashboard companyKey="magnira" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="transmittal" element={<Transmittal />} />
          <Route path="transmittal/:id" element={<Transmittal />} />
          <Route path="transmittals" element={<TransmittalList />} />

          <Route path="software-employee" element={<SoftwareEmployee />} />
          <Route path="hardware-employee" element={<HardwareEmployee />} />
          <Route
            path="/employee-drawing-report"
            element={<EmployeeDrawingReport />}
          />
          <Route
            path="/active-sessions"
            element={<ActiveSessions user={user} />}
          />
          {/* Permission-based access for Software/Hardware All Staff */}
          <Route
            path="software"
            element={
              canViewSoftwareAllStaff ? (
                <SoftwareAdmin />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="software/archived"
            element={
              canViewSoftwareAllStaff ? (
                <SoftwareArchived />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="software/renewal-history/:id"
            element={
              canViewSoftwareAllStaff ? (
                <SoftwareRenewalHistory />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hardware"
            element={
              canViewHardwareAllStaff ? (
                <HardwareAdmin />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hardware-destroyed"
            element={
              canViewHardwareAllStaff ? (
                <HardwareDestroyed />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="employee-review"
            element={
              !isClient && hasPermission("employeeReview", "view") ? (
                <EmployeeReview user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="employee-reviews"
            element={
              !isClient && hasPermission("employeeReview", "view") ? (
                <EmployeeReview user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="finance-entry" element={<FinanceEntry user={user} />} />
          <Route
            path="construction-report"
            element={<ConstructionReport user={user} />}
          />
          <Route
            path="/construction-reports/saved"
            element={<SavedConstructionReports />}
          />

          {/* CRM — visible to all non-client users */}
          <Route
            path="crm-dashboard"
            element={isClient ? <Navigate to="/" replace /> : <CrmDashboard />}
          />
          <Route
            path="leads"
            element={isClient ? <Navigate to="/" replace /> : <Leads />}
          />
          <Route
            path="campaign-master"
            element={
              isClient ? <Navigate to="/" replace /> : <CampaignMaster />
            }
          />
          <Route
            path="leads/:id"
            element={isClient ? <Navigate to="/" replace /> : <LeadDetail />}
          />
          <Route
            path="proposals"
            element={isClient ? <Navigate to="/" replace /> : <Proposals />}
          />
          <Route
            path="sent-emails/intro"
            element={
              isClient ? (
                <Navigate to="/" replace />
              ) : (
                <SentEmails emailType="intro" />
              )
            }
          />
          <Route
            path="sent-emails/portfolio"
            element={
              isClient ? (
                <Navigate to="/" replace />
              ) : (
                <SentEmails emailType="portfolio" />
              )
            }
          />
          <Route
            path="sent-emails/proposal"
            element={
              isClient ? (
                <Navigate to="/" replace />
              ) : (
                <SentEmails emailType="proposal" />
              )
            }
          />
          <Route
            path="wons-leads"
            element={isClient ? <Navigate to="/" replace /> : <WonLeads />}
          />
          <Route
            path="lost-leads"
            element={isClient ? <Navigate to="/" replace /> : <LostLeads />}
          />
          <Route
            path="reports"
            element={isClient ? <Navigate to="/" replace /> : <Reports />}
          />

          {/* Hiring Management routes for normal users */}
          <Route
            path="hr-dashboard"
            element={
              !isClient && hasPermission("hiringDashboard", "view") ? (
                <HRDashboard />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/job-openings"
            element={
              !isClient && hasPermission("jobOpenings", "view") ? (
                <JobOpenings />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/candidates"
            element={
              !isClient && hasPermission("candidates", "view") ? (
                <Candidates />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/candidates/:id"
            element={
              !isClient && hasPermission("candidates", "view") ? (
                <CandidateDetail />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/interviews"
            element={
              !isClient && hasPermission("interviews", "view") ? (
                <Interviews />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/offers"
            element={
              !isClient && hasPermission("offers", "view") ? (
                <Offers />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/joining"
            element={
              !isClient && hasPermission("joining", "view") ? (
                <Joining />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/closed-hiring"
            element={
              !isClient && hasPermission("closedHiring", "view") ? (
                <ClosedHiring />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="hr/recruitment-reports"
            element={
              !isClient && hasPermission("recruitmentReports", "view") ? (
                <RecruitmentReports />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="project-hours-summary"
            element={<ProjectHoursSummary />}
          />
          {/* ================= SALES ROUTES ================= */}

          <Route
            path="sales/dashboard"
            element={
              !isClient && hasPermission("salesDashboard", "view") ? (
                <SalesDashboard />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/services"
            element={
              !isClient && hasPermission("serviceMaster", "view") ? (
                <SalesMasterPage type="services" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/clients"
            element={
              !isClient && hasPermission("clientMaster", "view") ? (
                <SalesMasterPage type="clients" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/banks"
            element={
              !isClient && hasPermission("bankMaster", "view") ? (
                <SalesMasterPage type="banks" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/terms"
            element={
              !isClient && hasPermission("termsMaster", "view") ? (
                <SalesMasterPage type="terms" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/quotations"
            element={
              !isClient && hasPermission("quotations", "view") ? (
                <QuotationPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/proforma-invoices"
            element={
              !isClient && hasPermission("proformaInvoices", "view") ? (
                <ProformaInvoicePage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="sales/invoices"
            element={
              !isClient && hasPermission("invoices", "view") ? (
                <InvoicePage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          {/* ======================================================
    HR MANAGEMENT ROUTES FOR PERMISSION-BASED NORMAL USERS
====================================================== */}

          <Route
            path="admin/log-summary"
            element={
              !isClient && hasPermission("employeeLog", "viewAll") ? (
                <AdminLogSummary />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="admin/logs/:userId"
            element={
              !isClient && hasPermission("employeeLog", "viewAll") ? (
                <EmployeeLogDetail />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="leave-management"
            element={
              !isClient && hasPermission("leaveApplication", "approve") ? (
                <LeaveRequest />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="manage-employees"
            element={
              !isClient && hasPermission("manageEmployees", "view") ? (
                <ManageEmployees />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="admin-employee-review-detail"
            element={
              !isClient && hasPermission("employeeReview", "create") ? (
                <AdminEmployeeReviewDetail />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="role-permissions"
            element={
              !isClient && hasPermission("rolePermissions", "view") ? (
                <RolePermissionPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          {/* GST Reports - Non Admin */}
          <Route
            path="finance/reports/gst/r1-sales"
            element={
              !isClient &&
              (isAccountant || hasPermission("gstReports", "view")) ? (
                <GSTReport initialTab="sales" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="finance/reports/gst/3b-purchase"
            element={
              !isClient &&
              (isAccountant || hasPermission("gstReports", "view")) ? (
                <GSTReport initialTab="purchase" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="finance/reports/tds/payable"
            element={
              !isClient &&
              (isAccountant || hasPermission("tdsReports", "view")) ? (
                <TDSReport initialTab="payable" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="finance/reports/tds/receivable"
            element={
              !isClient &&
              (isAccountant || hasPermission("tdsReports", "view")) ? (
                <TDSReport initialTab="receivable" />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          {/* ================= PURCHASE ROUTES ================= */}

          <Route
            path="purchase/dashboard"
            element={
              !isClient && hasPermission("purchaseDashboard", "view") ? (
                <PurchaseDashboard />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="purchase/quotations"
            element={
              !isClient && hasPermission("purchaseQuotations", "view") ? (
                <PurchaseQuotationPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="purchase/proforma-invoices"
            element={
              !isClient && hasPermission("purchaseProformaInvoices", "view") ? (
                <PurchaseProformaInvoicePage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="purchase/invoices"
            element={
              !isClient && hasPermission("purchaseInvoices", "view") ? (
                <PurchaseInvoicePage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="purchase/vendors"
            element={
              !isClient && hasPermission("vendorMaster", "view") ? (
                <PurchaseVendorMaster />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* ================= END PURCHASE ROUTES ================= */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

/* ========================= APP CONTENT ========================= */

function AppContent({ user, handleLogout }) {
  const { subscribeToMessages } = useChatStore();

  useEffect(() => {
    if (user?._id) {
      connectSocket(user._id);
      initSocketNotifications();
      subscribeToMessages();
    }
  }, [user, subscribeToMessages]);

  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isAccountant = ["accountant", "accoutant", "finance"].includes(role);
  const isClient = role === "client";

  return (
    <>
      <Navbar user={user} onLogout={handleLogout} />
      {isAdmin ? (
        <AdminShell user={user} />
      ) : (
        <NonAdminShell
          user={user}
          isAccountant={isAccountant}
          isClient={isClient}
        />
      )}
    </>
  );
}

/* ============================ APP ============================= */

function App() {
  const { authUser, checkAuth, isCheckingAuth, logout } = useAuthStore();

  useEffect(() => {
    ensureNotificationPermission();
    checkAuth();
  }, [checkAuth]);

  if (isCheckingAuth) return <div>Checking user info...</div>;

  return (
    <>
      {/* Global ERP / Messenger front-screen popup */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={10}
        containerStyle={{
          top: 20,
          right: 20,
          zIndex: 999999,
        }}
        toastOptions={{
          duration: 6000,
        }}
      />

      <BrowserRouter>
        <Routes>
          {authUser ? (
            <Route
              path="/*"
              element={<AppContent user={authUser} handleLogout={logout} />}
            />
          ) : (
            <Route path="/" element={<LoginForm />} />
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
